import type { ManifoldToplevel } from 'manifold-3d';

type Manifold = InstanceType<ManifoldToplevel['Manifold']>;
type Mat4 = [number, number, number, number, number, number, number, number,
  number, number, number, number, number, number, number, number];

export type SectionAxis = 'x' | 'y' | 'z';

/** Section plane perpendicular to a SCAD axis at `offset`. Keeps the side
 *  below `offset` (the side above is cut away), or the opposite with `flip`. */
export interface SectionState {
  axis: SectionAxis;
  offset: number;
  flip: boolean;
}

/**
 * Three.js clipping plane (normal, constant) for a section, in Three world
 * space (Y-up; SCAD (x, y, z) = Three (x, -z, y)). Three keeps points with
 * normal·p + constant ≥ 0.
 */
export function sectionPlaneThree(s: SectionState): { normal: [number, number, number]; constant: number } {
  const sign = s.flip ? -1 : 1;
  const c = s.offset * sign;
  switch (s.axis) {
    case 'x': return { normal: [-sign, 0, 0], constant: c };   // keep x < offset
    case 'y': return { normal: [0, 0, sign], constant: c };    // keep SCAD y < offset  (y = -z₃)
    case 'z': return { normal: [0, -sign, 0], constant: c };   // keep SCAD z < offset  (z = y₃)
  }
}

// Rotations (cyclic axis permutations, column-major) that bring the section
// axis onto Z so Manifold.slice() can be used, and the inverse mapping of a
// slice point (u, v) at height h back to SCAD coordinates.
const TO_Z: Record<SectionAxis, Mat4 | null> = {
  // new = (y, z, x): e_x → (0,0,1), e_y → (1,0,0), e_z → (0,1,0)
  x: [0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1],
  // new = (z, x, y): e_x → (0,1,0), e_y → (0,0,1), e_z → (1,0,0)
  y: [0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1],
  z: null,
};
const BACK: Record<SectionAxis, (u: number, v: number, h: number) => [number, number, number]> = {
  x: (u, v, h) => [h, u, v],
  y: (u, v, h) => [v, h, u],
  z: (u, v, h) => [u, v, h],
};

/**
 * Exact cross-section of `manifolds` at the section plane, triangulated.
 * Returns SCAD-space triangles (9 floats each) and the total cut area (mm²).
 */
export function sliceCaps(
  wasm: ManifoldToplevel,
  manifolds: Manifold[],
  s: SectionState,
): { triangles: Float32Array; area: number } {
  if (manifolds.length === 0) return { triangles: new Float32Array(0), area: 0 };
  const toZ = TO_Z[s.axis];
  const back = BACK[s.axis];
  // Slice every part, then union the 2D sections so overlapping parts are
  // counted (and drawn) once.
  const slices = manifolds.map(m => {
    const rotated = toZ ? m.transform(toZ) : null;
    try {
      return (rotated ?? m).slice(s.offset);
    } finally {
      rotated?.delete();
    }
  });
  const merged = slices.length === 1 ? slices[0] : wasm.CrossSection.union(slices);
  try {
    const area = merged.area();
    const polys = merged.toPolygons();
    const out: number[] = [];
    if (polys.length > 0) {
      const pts: Array<[number, number]> = [];
      for (const poly of polys) for (const p of poly) pts.push([p[0], p[1]]);
      for (const tri of wasm.triangulate(polys)) {
        for (const idx of tri) {
          const [u, v] = pts[idx];
          out.push(...back(u, v, s.offset));
        }
      }
    }
    return { triangles: new Float32Array(out), area };
  } finally {
    if (merged !== slices[0]) merged.delete();
    for (const cs of slices) cs.delete();
  }
}
